import { Controller } from "@hotwired/stimulus"
import * as Log from "web-ui/log"

const defaultConfirmationText = "Changes you made will not be saved"

// Connects to data-controller="dynamic-form-discard-changes-confirmation"
export default class extends Controller {
  static values = {
    text: String
  }

  connect() {
    this.debug("Connect", { confirmationText: this.confirmationText })

    this.allowNavigation = false

    document.addEventListener("click", this.click, { capture: true })
    document.addEventListener("turbo:visit", this.turboVisit)
    window.addEventListener("beforeunload", this.beforeUnload)
    document.addEventListener("turbo:before-stream-render", this.beforeStreamRender)
  }

  disconnect() {
    this.debug("Disconnect")

    document.removeEventListener("click", this.click, { capture: true })
    document.removeEventListener("turbo:visit", this.turboVisit)
    window.removeEventListener("beforeunload", this.beforeUnload)
    document.removeEventListener("turbo:before-stream-render", this.beforeStreamRender)
  }

  click = (event) => {
    const target = event.target

    const targetId = target.id
    if (targetId === "cancel") {
      this.allowNavigation = true
      return
    }

    const discardChangesConfirmation = target.dataset.discardChangesConfirmation

    if (discardChangesConfirmation == "false") {
      return
    }

    if (!this.hasUnsubmittedChanges()) {
      return
    }

    const targetLink = event.target.closest("a")

    if (targetLink == null) {
      return
    }

    if (targetLink.target === "_blank") {
      return
    }

    const confirmed = confirm(this.confirmationText)

    if (confirmed) {
      this.allowNavigation = true
    } else {
      event.preventDefault()
      event.stopPropagation()
    }
  }

  // If the page being navigated to includes the meta tag to indicate that turbo
  // should reload the page, rather than proceeding with the turbo navigation,
  // then the page reload will trigger beforeunload.
  // At this point, we no longer need to protect against discarding changes because either the
  // form has been successfully submitted and the redirect is occurring, or the user clicked on
  // a turbo link and there weren't any unsaved changes.
  turboVisit = () => {
    window.removeEventListener("beforeunload", this.beforeUnload)
  }

  beforeUnload = (event) => {
    if (!this.hasUnsubmittedChanges()) {
      return
    }

    if (this.allowNavigation) {
      return
    }

    event.preventDefault()

    // Support for older browsers
    event.returnValue = this.confirmationText
    return this.confirmationText
  }

  hasUnsubmittedChanges() {
    const dynamicFormController = this.application.getControllerForElementAndIdentifier(this.element, "dynamic-form")

    return dynamicFormController.hasUnsubmittedChanges()
  }

  beforeStreamRender = (event) => {
    const turboStreamElement = event.detail.newStream
    const action = turboStreamElement.getAttribute("action")

    if (action == "redirect") {
      window.removeEventListener("beforeunload", this.beforeUnload)
    }
  }

  get confirmationText() {
    return this.textValue || defaultConfirmationText
  }

  debug(message, data) {
    Log.debug("Dynamic Form Discard Changes Confirmation - " + message, data)
  }
}
